/** Extract a displayable error string from an API error response.
 *  Handles FastAPI validation errors (array of {msg, loc, ...}) and plain string details.
 *  Also handles network-level errors (timeout, connection refused) with no HTTP response. */
export function extractErrorMessage(err: any, fallback = 'An error occurred'): string {
  // Network-level errors (no response received)
  if (err && !err.response) {
    if (err.code === 'ECONNABORTED') return 'Request timed out — the server took too long to respond. Try again or check your connection.';
    if (err.message && typeof err.message === 'string') return err.message;
    return fallback;
  }
  const data = err?.response?.data;
  const detail = data?.detail;
  // Some endpoints return the message on the body itself, not under `detail`.
  if (!detail) {
    if (typeof data === 'string' && data.trim()) return data;
    const bodyMsg = data?.message || data?.error;
    if (typeof bodyMsg === 'string' && bodyMsg.trim()) return bodyMsg;
    return fallback;
  }
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e: any) => (typeof e === 'string' ? e : e.msg || JSON.stringify(e))).join('; ');
  }
  // Structured object detail (e.g. {error, code, message}) — never String() it,
  // that renders the literal text "[object Object]". Dig for a human message first.
  if (detail && typeof detail === 'object') {
    const msg = detail.message || detail.msg || detail.error || detail.reason;
    if (typeof msg === 'string' && msg.trim()) return msg;
    try {
      return JSON.stringify(detail);
    } catch {
      return fallback;
    }
  }
  return String(detail);
}

/** Currency symbol map — shared with useJurisdiction. */
export function getCurrencySymbol(code: string): string {
  const symbols: Record<string, string> = {
    NGN: '\u20A6',    // ₦
    GHS: 'GH\u20B5', // GH₵
    ZAR: 'R',
    EUR: '\u20AC',    // €
    SEK: 'kr',
    USD: '$',
    GBP: '\u00A3',    // £
    KES: 'KSh',
    EGP: 'E£',   // E£
    MAD: 'DH',
    RWF: 'FRw',
  };
  return symbols[code] || code;
}

/** Locale map per currency code for correct thousand/decimal separators. */
export function getLocaleForCurrency(currencyCode: string): string {
  const locales: Record<string, string> = {
    NGN: 'en-NG',
    GHS: 'en-GH',
    ZAR: 'en-ZA',
    EUR: 'de-DE',
    SEK: 'sv-SE',
    USD: 'en-US',
    GBP: 'en-GB',
    KES: 'en-KE',
    EGP: 'en-EG',
    MAD: 'fr-MA',
    RWF: 'en-RW',
  };
  return locales[currencyCode] || 'en-US';
}

/** Format a monetary value with the correct symbol and locale for the given currency. */
export function formatCurrency(
  value: string | number | null | undefined,
  currencyCode: string = 'NGN',
): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (isNaN(num)) return '—';
  const symbol = getCurrencySymbol(currencyCode);
  const locale = getLocaleForCurrency(currencyCode);
  return `${symbol}${num.toLocaleString(locale, { minimumFractionDigits: 2 })}`;
}

/** Format a monetary value with full digits (no M/K abbreviation) using a pre-resolved symbol.
 *  Use this for KPI cards, chart tooltips, and any display that needs the complete number.
 *  Pass currencyCode OR symbol — currencyCode takes precedence for locale resolution. */
export function formatDisplayCurrency(
  value: string | number | null | undefined,
  symbolOrCode: string = 'NGN',
): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (isNaN(num)) return '—';
  // If a currency code is passed (3 uppercase letters), resolve symbol + locale
  const isCode = /^[A-Z]{3}$/.test(symbolOrCode);
  const symbol = isCode ? getCurrencySymbol(symbolOrCode) : symbolOrCode;
  const locale = isCode ? getLocaleForCurrency(symbolOrCode) : 'en-US';
  return `${symbol}${num.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** @deprecated Use formatCurrency(value, currencyCode) instead. */
export function formatNGN(value: string | number | null | undefined): string {
  return formatCurrency(value, 'NGN');
}

/** Format a number for display inside a currency input (no symbol, with commas). */
export function formatCurrencyInput(value: number | string, currencyCode: string = 'NGN'): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num) || num === 0) return '';
  return num.toLocaleString(getLocaleForCurrency(currencyCode));
}

/** Parse a formatted currency string back to a number (strips commas). */
export function parseCurrency(value: string): number {
  return parseFloat(value.replace(/,/g, '')) || 0;
}

// ── Company display format defaults (set by CompanyFormatsProvider) ──
let _defaultDateFormat = 'DD MMM YYYY';
let _defaultTimeFormat = '24h';

/** Called by CompanyFormatsProvider to sync company prefs into format utils. */
export function setDefaultFormats(dateFormat: string, timeFormat: string) {
  _defaultDateFormat = dateFormat;
  _defaultTimeFormat = timeFormat;
}

export function getDefaultDateFormat() { return _defaultDateFormat; }
export function getDefaultTimeFormat() { return _defaultTimeFormat; }

/**
 * Parse a date string without timezone shift for date-only inputs.
 *
 * `new Date("2026-12-25")` is spec'd to parse as UTC midnight, so when
 * `getDate()` / `getMonth()` / `getFullYear()` are read back in a negative-
 * offset timezone (e.g. the Americas), the displayed date shifts back one
 * day — "2026-12-25" renders as "Dec 24". For date-only strings (no time
 * component) we build a local-time Date from the parts instead.
 *
 * ISO datetimes with a time component keep the native Date parsing so
 * timezone-aware values (created_at, updated_at) render correctly in the
 * user's local zone.
 */
export function parseLocalDate(value: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(
      Number(value.slice(0, 4)),
      Number(value.slice(5, 7)) - 1,
      Number(value.slice(8, 10)),
    );
  }
  return new Date(value);
}

/**
 * Format a date string according to a company date format pattern.
 * Supported patterns: DD-MM-YYYY, MM-DD-YYYY, YYYY-MM-DD,
 *   DD/MM/YYYY, MM/DD/YYYY, DD MMM YYYY, MMM DD, YYYY
 * Uses the company default if no explicit format is passed.
 *
 * Date-only strings ("YYYY-MM-DD") are parsed as local time via
 * `parseLocalDate` — see that helper for the rationale.
 */
export function formatDate(value: string, dateFormat?: string): string {
  if (!value) return '';
  const d = parseLocalDate(value);
  if (isNaN(d.getTime())) return value;

  const fmt = dateFormat || _defaultDateFormat;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear());
  const shortMonth = d.toLocaleDateString('en-GB', { month: 'short' });

  switch (fmt) {
    case 'DD-MM-YYYY':  return `${day}-${month}-${year}`;
    case 'MM-DD-YYYY':  return `${month}-${day}-${year}`;
    case 'YYYY-MM-DD':  return `${year}-${month}-${day}`;
    case 'DD/MM/YYYY':  return `${day}/${month}/${year}`;
    case 'MM/DD/YYYY':  return `${month}/${day}/${year}`;
    case 'MMM DD, YYYY': return `${shortMonth} ${day}, ${year}`;
    case 'DD MMM YYYY':
    default:             return `${day} ${shortMonth} ${year}`;
  }
}

/** Format a time string or Date according to company time format (12h / 24h). */
export function formatTime(value: string | Date, timeFormat?: string): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return typeof value === 'string' ? value : '';

  const fmt = timeFormat || _defaultTimeFormat;
  if (fmt === '12h') {
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  }
  // Default: 24h
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Format a datetime string using both company date and time format. */
export function formatDateTime(value: string, dateFormat?: string, timeFormat?: string): string {
  if (!value) return '';
  return `${formatDate(value, dateFormat)} ${formatTime(value, timeFormat)}`;
}

export function formatMonth(month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return months[month - 1] || '';
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}


/**
 * Resolve a relative upload path (e.g. "/uploads/images/logos/...") to a full URL
 * using the VITE_API_URL. Returns the value unchanged if already absolute or empty.
 */
export function resolveUploadUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  if (path.startsWith('http')) return path;
  const base = import.meta.env.VITE_API_URL || '';
  return `${base}${path}`;
}
